import { configureStore } from '@reduxjs/toolkit';

import { addTagTypes as apiTagTypes } from 'api/api';
import logger from 'redux-logger';
import { GeneratedCombineState } from 'utils/store';

import { addTagTypes as paginationTagTypes } from 'api/pagination.api';
import { paginationSplitApi } from 'api/pagination.template.api';
import { templateSplitApi } from 'api/template.api';

import { rtkQueryErrorLogger } from './errorLoggerMiddleware';
import { sessionSlice } from './session.slice';
import { createTaskPopupSlice } from 'features/createTaskPopup/createTaskPopup.slice';
import { employeePersistedReducer } from 'features/employee/employee.slice';
import { flagsPersistedReducer } from 'features/flags/flags.slice';
import { joinEmployeeFlowSlice } from 'features/joinEmployeeFlow/joinEmployeeFlow.slice';
import { navigationSidebarPersistedReducer } from 'features/navigationSidebar/navigationSidebar.slice';
import { onboardingPersistedReducer } from 'features/onboarding/onboarding.slice';
import { vacationTaskTemplateSlice } from 'features/taskTemplate/vacation/vacationTaskTemplate.slice';
import { tasksPersistedReducer } from 'features/tasks/tasks.slice';
import { universalDocumentSlice } from 'features/universalDocument/universalDocument.slice';
import { userConfigPersistedReducer } from 'features/userConfig/userConfig.slice';

export const store = configureStore({
  reducer: {
    sidebar: navigationSidebarPersistedReducer,
    employee: employeePersistedReducer,
    userConfig: userConfigPersistedReducer,
    task: tasksPersistedReducer,
    vacationTaskTemplate: vacationTaskTemplateSlice.reducer,
    session: sessionSlice.reducer,
    createTaskPopupSlice: createTaskPopupSlice.reducer,
    [templateSplitApi.reducerPath]: templateSplitApi.reducer,
    [paginationSplitApi.reducerPath]: paginationSplitApi.reducer,
    universalDocument: universalDocumentSlice.reducer,
    flags: flagsPersistedReducer,
    onboarding: onboardingPersistedReducer,
    [joinEmployeeFlowSlice.name]: joinEmployeeFlowSlice.reducer,
  },
  middleware: (getDefaultMiddleware) => {
    const middleware = getDefaultMiddleware({ serializableCheck: false });

    middleware.push(rtkQueryErrorLogger);

    middleware.push(templateSplitApi.middleware);
    middleware.push(paginationSplitApi.middleware);

    if (STORE_LOGGER_ENABLED) {
      middleware.push(logger);
    }

    return middleware;
  },
});

export type AppDispatch = typeof store.dispatch;

export type AppApiState = GeneratedCombineState<
  (typeof apiTagTypes)[number],
  'api'
>;

export type AppPaginationState = GeneratedCombineState<
  (typeof paginationTagTypes)[number],
  'paginationSplitApi'
>;

export interface RootState extends ReturnType<typeof store.getState> {
  api: AppApiState;
  paginationSplitApi: AppPaginationState;
}

export const selectAppApi = (state: RootState) => state.api;

export const selectAppPagination = (state: RootState) =>
  state.paginationSplitApi;
